import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProperty } from "@/context/PropertyContext";
import api from "@/lib/api";
import { eur, fechaCorta } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { Wallet, Receipt, TrendingUp, AlertTriangle, Users, DoorOpen, Settings, ArrowUp, ArrowDown, RotateCcw, FileSignature, CheckCircle2, BedDouble } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, LabelList,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const DASHBOARD_LAYOUT_KEY = "rentapiso.dashboard.layout.v1";
const DEFAULT_PANELS = [
  { id: "kpis", label: "Indicadores principales", visible: true },
  { id: "alerts", label: "Alertas de gestión", visible: true },
  { id: "rooms", label: "Habitaciones", visible: true },
  { id: "charts", label: "Gráficos y ocupación", visible: true },
  { id: "documents", label: "Contratos sin documento", visible: true },
  { id: "pending", label: "Cobros pendientes", visible: true },
  { id: "expirations", label: "Vencimientos de contratos", visible: true },
];

function loadDashboardPanels() {
  try {
    const saved = JSON.parse(window.localStorage.getItem(DASHBOARD_LAYOUT_KEY) || "null");
    if (!Array.isArray(saved)) return DEFAULT_PANELS;
    const byId = new Map(saved.map((p) => [p.id, p]));
    const known = saved
      .filter((p) => DEFAULT_PANELS.some((d) => d.id === p.id))
      .map((p) => ({ ...DEFAULT_PANELS.find((d) => d.id === p.id), visible: p.visible !== false }));
    const missing = DEFAULT_PANELS.filter((p) => !byId.has(p.id));
    return [...known, ...missing];
  } catch {
    return DEFAULT_PANELS;
  }
}

export default function Dashboard() {
  const { selectedId, selected } = useProperty();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customizing, setCustomizing] = useState(false);
  const [panels, setPanels] = useState(loadDashboardPanels);
  const [activePanelId, setActivePanelId] = useState(null);
  const [selectedAlertType, setSelectedAlertType] = useState(null);

  useEffect(() => {
    if (!selectedId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api
      .get(`/dashboard?property_id=${selectedId}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [selectedId]);

  useEffect(() => {
    window.localStorage.setItem(DASHBOARD_LAYOUT_KEY, JSON.stringify(panels));
  }, [panels]);

  const movePanel = (id, direction) => {
    setActivePanelId(id);
    setPanels((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      const nextIdx = idx + direction;
      if (idx < 0 || nextIdx < 0 || nextIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[nextIdx]] = [next[nextIdx], next[idx]];
      return next;
    });
  };

  const togglePanel = (id) => {
    setActivePanelId(id);
    setPanels((prev) => prev.map((p) => (p.id === id ? { ...p, visible: !p.visible } : p)));
  };

  const resetPanels = () => setPanels(DEFAULT_PANELS);
  const activePanelIndex = panels.findIndex((p) => p.id === activePanelId);

  if (!selectedId) {
    return (
      <div>
        <PageHeader title="Panel" subtitle="Resumen general" />
        <div className="data-card p-10 text-center text-muted-foreground">
          No hay pisos todavía. Crea uno en la sección <strong>Pisos</strong> para empezar.
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div>
        <PageHeader title="Panel" subtitle="Resumen general" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-md" />
          ))}
        </div>
        <Skeleton className="h-80 mt-6 rounded-md" />
      </div>
    );
  }

  const chartOperativo = (data.serie_anual || []).map((s) => ({
    anio: s.anio,
    Ingresos: Math.round(s.ingresos),
    Suministros: Math.round(s.gastos_suministros || 0),
  }));

  const totalIngresos = Math.round(data.ingresos || 0);
  const totalGastos = Math.round(data.gastos || 0);
  const totalBeneficio = Math.round((data.ingresos || 0) - (data.gastos || 0));
  const totalsBars = [
    { name: "Ingresos", value: totalIngresos, color: "hsl(160 84% 39%)" },
    { name: "Gastos", value: totalGastos, color: "hsl(4 100% 60%)" },
    { name: "Beneficio", value: totalBeneficio, color: "hsl(217 91% 60%)" },
  ];
  const chartGrid = "hsl(var(--border))";
  const chartTooltip = {
    borderRadius: 8,
    border: "1px solid hsl(var(--border))",
    background: "hsl(var(--popover))",
    color: "hsl(var(--popover-foreground))",
    fontSize: 13,
  };
  const chartTick = { fontSize: 12, fill: "hsl(var(--muted-foreground))" };

  const freeRooms = (data.habitaciones_estado || []).filter((h) => !h.ocupada && !h.ocupada_futuro);
  const futureRooms = (data.habitaciones_estado || []).filter((h) => !h.ocupada && h.ocupada_futuro);
  const alertDetails = {
    cobros: {
      title: "Cobros vencidos",
      description: "Pagos pendientes cuya fecha ya ha vencido.",
      action: "Ir a pagos",
      route: "/pagos",
      items: (data.pendientes || []).map((p) => ({
        id: p.id,
        title: p.tenant_nombre || "Inquilino sin nombre",
        subtitle: `Vencía: ${fechaCorta(p.fecha)} · ${eur(p.importe)}`,
      })),
      empty: "No hay cobros vencidos pendientes.",
    },
    habitaciones: {
      title: "Habitaciones libres y futuras",
      description: "Habitaciones sin contrato activo hoy, separando las que ya tienen contrato futuro.",
      action: "Ir a contratos",
      route: "/contratos",
      items: [...freeRooms, ...futureRooms].map((h) => ({
        id: `${h.property_id}-${h.habitacion}`,
        title: `Hab. ${h.habitacion}`,
        subtitle: h.ocupada_futuro
          ? `Ocupada a futuro desde ${fechaCorta(h.future_fecha_inicio)}${h.future_tenant_nombre ? ` · ${h.future_tenant_nombre}` : ""}`
          : `Libre · ${h.property_nombre || selected?.nombre || "Piso actual"}`,
      })),
      empty: "No hay habitaciones libres ni ocupadas a futuro.",
    },
    documentos: {
      title: "Contratos sin documento guardado",
      description: "Contratos activos que todavía no tienen una foto/documento guardado en histórico.",
      action: "Ir a contratos",
      route: "/contratos",
      items: (data.contratos_sin_documento || []).map((c) => ({
        id: c.id,
        title: `${c.tenant_nombre || "Inquilino sin nombre"}${c.habitacion ? ` · Hab. ${c.habitacion}` : ""}`,
        subtitle: `Inicio: ${fechaCorta(c.fecha_inicio)}`,
      })),
      empty: "Todos los contratos activos tienen documento guardado.",
    },
    inquilinos: {
      title: "Inquilinos con datos incompletos",
      description: "Inquilinos activos a los que les falta información útil para contratos o gestión.",
      action: "Ir a inquilinos",
      route: "/inquilinos",
      items: (data.inquilinos_datos_incompletos || []).map((t) => ({
        id: t.id,
        title: t.nombre || "Inquilino sin nombre",
        subtitle: `Falta: ${(t.faltan || []).join(", ")}`,
      })),
      empty: "No hay inquilinos activos con datos incompletos.",
    },
  };
  const selectedAlertDetail = selectedAlertType ? alertDetails[selectedAlertType] : null;

  const panelContent = {
    kpis: (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard label="Ingresos cobrados" value={eur(data.ingresos)} icon={Wallet} accent="success" testid="kpi-ingresos" />
        <StatCard label="Gastos suministros" value={eur(data.gastos_suministros ?? data.gastos)} icon={Receipt} accent="danger" sub={`Inversión: ${eur(data.gastos_inversion || 0)}`} testid="kpi-gastos" />
        <StatCard
          label="Balance operativo"
          value={eur(data.balance)}
          icon={TrendingUp}
          accent={data.balance >= 0 ? "success" : "danger"}
          sub="Ingresos − suministros"
          testid="kpi-balance"
        />
        <StatCard
          label="Cobros pendientes"
          value={eur(data.total_pendiente)}
          icon={AlertTriangle}
          accent="warning"
          sub={`${data.pendientes.length} pago(s) vencido(s)`}
          testid="kpi-pendientes"
        />
      </div>
    ),
    alerts: (
      <div className="data-card p-5" data-testid="dashboard-alerts-card">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-4.5 w-4.5 text-warning" />
          <h3 className="font-display font-medium text-lg">Alertas de gestión</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {(data.alertas || []).map((a) => (
            <button
              key={a.tipo}
              type="button"
              onClick={() => setSelectedAlertType(a.tipo)}
              className={`rounded-md border p-3 text-left transition-colors hover:border-primary ${a.nivel === "ok" ? "border-success/20 bg-success/5" : a.nivel === "warning" ? "border-warning/25 bg-warning/5" : "border-border bg-secondary/30"}`}
            >
              <div className="flex items-start gap-2">
                {a.nivel === "ok" ? <CheckCircle2 className="h-4 w-4 text-success mt-0.5" /> : <AlertTriangle className={`h-4 w-4 mt-0.5 ${a.nivel === "warning" ? "text-warning" : "text-primary"}`} />}
                <p className="text-sm font-medium leading-snug">{a.texto}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Ver detalle</p>
            </button>
          ))}
        </div>
      </div>
    ),
    rooms: (
      <div className="data-card p-5" data-testid="dashboard-rooms-card">
        <div className="flex items-center gap-2 mb-4">
          <BedDouble className="h-4.5 w-4.5 text-primary" />
          <h3 className="font-display font-medium text-lg">Habitaciones</h3>
        </div>
        {(data.habitaciones_estado || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No hay habitaciones configuradas en este piso.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {(data.habitaciones_estado || []).map((h) => (
              <div key={`${h.property_id}-${h.habitacion}`} className={`rounded-md border p-3 ${h.ocupada ? "border-success/25 bg-success/5" : h.ocupada_futuro ? "border-primary/25 bg-primary/5" : "border-border bg-secondary/30"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">Hab. {h.habitacion}{h.property_nombre && !selectedId ? ` · ${h.property_nombre}` : ""}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{h.ocupada ? h.tenant_nombre : h.ocupada_futuro ? h.future_tenant_nombre || "Contrato futuro" : "Libre"}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${h.ocupada ? "bg-success/15 text-success" : h.ocupada_futuro ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {h.ocupada ? "Ocupada" : h.ocupada_futuro ? "Ocupada a futuro" : "Libre"}
                  </span>
                </div>
                {h.ocupada && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Fin inicial: {fechaCorta(h.fecha_fin_inicial)}{h.prorroga_indefinida ? " · Prórroga" : ""}
                  </p>
                )}
                {h.ocupada && h.ocupada_futuro && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Próximo: {fechaCorta(h.future_fecha_inicio)}{h.future_tenant_nombre ? ` · ${h.future_tenant_nombre}` : ""}
                  </p>
                )}
                {!h.ocupada && h.ocupada_futuro && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Inicio futuro: {fechaCorta(h.future_fecha_inicio)}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    charts: (
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="data-card p-5 xl:col-span-2" data-testid="chart-ingresos-gastos">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-medium text-lg">Ingresos vs Suministros (operativo)</h3>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartOperativo} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartGrid} />
              <XAxis dataKey="anio" tick={{ ...chartTick, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ ...chartTick, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={50} />
              <Tooltip
                formatter={(v) => eur(v)}
                contentStyle={chartTooltip}
              />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Bar dataKey="Ingresos" fill="hsl(160 84% 39%)" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Suministros" fill="hsl(4 100% 60%)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-6 flex items-center justify-between mb-4">
            <h3 className="font-display font-medium text-lg">Totales (incluye inversiones/generales)</h3>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={totalsBars} layout="vertical" barCategoryGap={18}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={chartGrid} />
              <XAxis type="number" tick={{ ...chartTick, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={chartTick} axisLine={false} tickLine={false} width={80} />
              <Tooltip formatter={(v) => eur(v)} contentStyle={chartTooltip} />
              <Bar dataKey="value" radius={[6, 6, 6, 6]}>
                {totalsBars.map((row) => (
                  <Cell key={row.name} fill={row.color} />
                ))}
                <LabelList dataKey="value" position="insideRight" formatter={(v) => eur(v)} fill="#0A0A0A" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="space-y-4">
          <div className="data-card p-5">
            <p className="overline mb-3">Ocupación</p>
            <div className="flex items-end gap-2">
              <DoorOpen className="h-7 w-7 text-primary mb-1" strokeWidth={1.75} />
              <span className="font-mono text-3xl font-semibold">{data.contratos_activos}</span>
              <span className="text-muted-foreground mb-1">/ {data.total_habitaciones || "—"} hab.</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Contratos activos hoy</p>
          </div>
          <div className="data-card p-5">
            <p className="overline mb-3">Inquilinos</p>
            <div className="flex items-end gap-2">
              <Users className="h-7 w-7 text-primary mb-1" strokeWidth={1.75} />
              <span className="font-mono text-3xl font-semibold">{data.num_inquilinos}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Registrados en este piso</p>
          </div>
        </div>
      </div>
    ),
    documents: (
      <div className="data-card p-5" data-testid="dashboard-missing-documents-card">
        <div className="flex items-center gap-2 mb-4">
          <FileSignature className="h-4.5 w-4.5 text-primary" />
          <h3 className="font-display font-medium text-lg">Contratos activos sin documento</h3>
        </div>
        {(data.contratos_sin_documento || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">Todos los contratos activos tienen documento guardado.</p>
        ) : (
          <div className="divide-y divide-border">
            {(data.contratos_sin_documento || []).slice(0, 12).map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="text-sm font-medium">{c.tenant_nombre}{c.habitacion ? ` · Hab. ${c.habitacion}` : ""}</p>
                  <p className="text-xs text-muted-foreground">Inicio: {fechaCorta(c.fecha_inicio)}</p>
                </div>
                <span className="text-xs font-medium text-warning">Sin foto</span>
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    pending: (
      <div className="data-card p-5" data-testid="pending-payments-card">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-4.5 w-4.5 text-warning" />
          <h3 className="font-display font-medium text-lg">Cobros pendientes</h3>
        </div>
        {data.pendientes.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">¡Todo al día! No hay cobros vencidos pendientes. ✓</p>
        ) : (
          <div className="divide-y divide-border">
            {data.pendientes.slice(0, 12).map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="text-sm font-medium">{p.tenant_nombre}</p>
                  <p className="text-xs text-muted-foreground">Vencía: {fechaCorta(p.fecha)}</p>
                </div>
                <span className="font-mono text-sm font-semibold text-warning">{eur(p.importe)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    ),
    expirations: (
      <div className="data-card p-5" data-testid="contract-expirations-card">
        <div className="flex items-center gap-2 mb-4">
          <DoorOpen className="h-4.5 w-4.5 text-primary" />
          <h3 className="font-display font-medium text-lg">Vencimientos de contratos</h3>
        </div>
        {(data.contratos_vigencia || []).length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No hay contratos próximos con vencimiento calculable.</p>
        ) : (
          <div className="divide-y divide-border">
            {(data.contratos_vigencia || []).slice(0, 12).map((c) => (
              <div key={c.id} className="flex items-center justify-between py-2.5">
                <div>
                  <p className="text-sm font-medium">{c.tenant_nombre}{c.habitacion ? ` · Hab. ${c.habitacion}` : ""}</p>
                  <p className="text-xs text-muted-foreground">
                    {c.fecha_fin_tipo === "inicial" ? "Fin inicial" : "Fin"}: {fechaCorta(c.fecha_fin)}{c.fecha_fin_tipo === "teorica" ? " (teórica)" : ""}
                  </p>
                </div>
                <span className={`font-mono text-sm font-semibold ${c.dias_restantes <= 30 ? "text-warning" : "text-muted-foreground"}`}>
                  {typeof c.dias_restantes === "number" ? `${c.dias_restantes} d` : "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    ),
  };

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Panel" subtitle={selected?.nombre || "Resumen general"}>
        <Button variant="outline" onClick={() => setCustomizing((v) => !v)} data-testid="dashboard-customize-button">
          <Settings className="h-4 w-4 mr-1.5" /> Personalizar
        </Button>
      </PageHeader>

      {customizing && (
        <div className="data-card p-4 mb-4" data-testid="dashboard-customize-panel">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <p className="font-medium">Paneles visibles</p>
              <p className="text-xs text-muted-foreground">Cambia el orden con las flechas o desmarca lo que no quieras ver.</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={resetPanels}>
                <RotateCcw className="h-4 w-4 mr-1.5" /> Restaurar
              </Button>
              <Button size="sm" onClick={() => setCustomizing(false)}>
                Cerrar
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {panels.map((panel, index) => (
              <div
                key={panel.id}
                role="button"
                tabIndex={0}
                onClick={() => setActivePanelId(panel.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") setActivePanelId(panel.id);
                }}
                className={`flex items-center gap-2 rounded-md border p-2.5 transition-colors ${activePanelId === panel.id ? "border-primary bg-primary/10" : "border-border"}`}
              >
                <input type="checkbox" checked={panel.visible} onChange={() => togglePanel(panel.id)} onClick={(e) => e.stopPropagation()} className="h-4 w-4" />
                <span className="flex-1 text-sm font-medium">{panel.label}</span>
                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); movePanel(panel.id, -1); }} disabled={index === 0}>
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); movePanel(panel.id, 1); }} disabled={index === panels.length - 1}>
                  <ArrowDown className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3 sm:hidden">
            <Button variant="outline" size="sm" onClick={() => movePanel(activePanelId, -1)} disabled={!activePanelId || activePanelIndex <= 0} className="flex-1">
              <ArrowUp className="h-4 w-4 mr-1.5" /> Subir seleccionado
            </Button>
            <Button variant="outline" size="sm" onClick={() => movePanel(activePanelId, 1)} disabled={!activePanelId || activePanelIndex < 0 || activePanelIndex >= panels.length - 1} className="flex-1">
              <ArrowDown className="h-4 w-4 mr-1.5" /> Bajar seleccionado
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {panels.filter((panel) => panel.visible).map((panel) => (
          <React.Fragment key={panel.id}>{panelContent[panel.id]}</React.Fragment>
        ))}
      </div>

      <Dialog open={!!selectedAlertDetail} onOpenChange={(open) => !open && setSelectedAlertType(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-display">{selectedAlertDetail?.title}</DialogTitle>
            <DialogDescription>{selectedAlertDetail?.description}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-y-auto rounded-md border border-border">
            {(selectedAlertDetail?.items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">{selectedAlertDetail?.empty}</p>
            ) : (
              <div className="divide-y divide-border">
                {selectedAlertDetail.items.map((item) => (
                  <div key={item.id} className="p-3">
                    <p className="text-sm font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.subtitle}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedAlertType(null)}>Cerrar</Button>
            <Button onClick={() => { const route = selectedAlertDetail?.route; setSelectedAlertType(null); if (route) navigate(route); }}>
              {selectedAlertDetail?.action || "Ir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
